import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import NavUser from "../components/NavUser";
import "./TravelerProfile.css";
import Swal from "sweetalert2";
import { getCoinBalance, redeemCoins } from "../services/coinTransactionsService";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaReceipt,
  FaShieldAlt,
  FaHotel,
  FaUserFriends,
  FaStar,
  FaPassport,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaChartLine,
  FaWalking,
  FaCheckCircle,
  FaFilter,
  FaCompass,
  FaUserCog,
} from "react-icons/fa";
import {
  getMyReferrals,
  generateReferralCode,
} from "../services/referralService";
import { 
  getListingsByOwner, 
  getBookingsByOwner, 
  getTotalPayout,
  getReviewsByOwner
} from "../services/hotelOwnerService";
import { getFileUrl } from "../utils/imageUrl";

Modal.setAppElement("#root");

function TravelerProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });
  const [bookings, setBookings] = useState({
    upcoming: [],
    past: [],
    cancelled: [],
  });
  const [activePage, setActivePage] = useState("upcoming");
  const [agentStatus, setAgentStatus] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [agentDetails, setAgentDetails] = useState(null);
  const [agentHotels, setAgentHotels] = useState([]);
  const [agentGuides, setAgentGuides] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [coinBalance, setCoinBalance] = useState(0);
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  
  // Modals
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailUserId, setEmailUserId] = useState(null);
  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [bookingDates, setBookingDates] = useState([]);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [limitReqReason, setLimitReqReason] = useState("");
  const [limitReqLoading, setLimitReqLoading] = useState(false);
  const [agentHotelLimit, setAgentHotelLimit] = useState(null);
  const [agentGuideLimit, setAgentGuideLimit] = useState(null);
  const [agentLimitType, setAgentLimitType] = useState("hotel"); // "hotel" | "guide"

  const { id: paramUserId } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("traveler"); // "traveler", "guide", "hotel", "agent"

  const isHotelOwnerRole = (u) => 
    u?.role_id === 3 || 
    u?.role_id === 1 || 
    u?.Role?.name === "Hotel Owner" || 
    u?.role === "Hotel Owner" || 
    u?.role === "hotel_owner";

  const isGuideRole = (u) => 
    u?.role_id === 2 || 
    (u?.role_id === 4 && u?.Role?.name !== "Admin") || 
    u?.Role?.name === "Guide" || 
    u?.role === "Guide" || 
    u?.role === "guide";

  const isApprovedRole = (u) => {
    if (!u) return false;
    if (u.status_id === 1 || u.status_id === 4 || u.status_id === 5) return true;
    const sName = u.Status?.name || u.status?.name || (typeof u.status === 'string' ? u.status : null);
    if (sName && ["Approved", "Active", "approved", "active"].includes(sName)) return true;
    if ((u.role_id === 2 || u.role_id === 3 || u.role_id === 1 || u.Role?.name === "Guide" || u.Role?.name === "Hotel Owner") && u.status_id !== 3 && u.status_id !== 2) {
      return true;
    }
    return false;
  };
  const isAgentRole = (u, isAg, targetId) => {
    if (u?.role_id === 6 || u?.Role?.name === "Traveler Agent" || u?.Role?.name === "Agent") return true;
    const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");
    if (isAg && (!targetId || String(targetId) === String(loggedUser?.id))) return true;
    return false;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam && ["traveler", "guide", "hotel", "agent"].includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (user) {
      if (isHotelOwnerRole(user) && isApprovedRole(user)) {
        setActiveTab("hotel");
      } else if (isGuideRole(user) && isApprovedRole(user)) {
        setActiveTab("guide");
      } else if (isAgentRole(user, isAgent, paramUserId)) {
        setActiveTab("agent");
      } else {
        setActiveTab("traveler");
      }
    }
  }, [location, user, isAgent, paramUserId]);
  
  // Guide State
  const [guideTours, setGuideTours] = useState([]);
  const [guideBookings, setGuideBookings] = useState([]);
  const [guideStats, setGuideStats] = useState({ activeListings: 0, bookingsThisWeek: 0 });
  const [guideId, setGuideId] = useState(null);
  const [guideReviews, setGuideReviews] = useState([]);
  const [guideTotalReviews, setGuideTotalReviews] = useState(0);
  const [guideAverageRating, setGuideAverageRating] = useState(0);
  const [selectedGuideTourFilter, setSelectedGuideTourFilter] = useState("all");
  const [selectedGuideRatingFilter, setSelectedGuideRatingFilter] = useState("all");

  // Hotel State
  const [hotelListings, setHotelListings] = useState([]);
  const [hotelBookings, setHotelBookings] = useState([]);
  const [hotelPayout, setHotelPayout] = useState(0);
  const [hotelReviews, setHotelReviews] = useState([]);
  const [hotelTotalReviews, setHotelTotalReviews] = useState(0);
  const [hotelAverageRating, setHotelAverageRating] = useState(0);
  const [selectedHotelListingFilter, setSelectedHotelListingFilter] = useState("all");
  const [selectedHotelRatingFilter, setSelectedHotelRatingFilter] = useState("all");

  // Format Review Date
  const formatReviewDate = (dateStr) => {
    if (!dateStr) return "Recent stay";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "Recent stay";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch (e) {
      return "Recent stay";
    }
  };

  // Render Star Rating
  const renderStarRating = (rating) => {
    const num = Math.round(Number(rating) || 5);
    return Array.from({ length: 5 }).map((_, i) => (
      <FaStar key={i} color={i < num ? "#f59e0b" : "#cbd5e1"} size={16} />
    ));
  };

  // Filtered Reviews for Guide
  const filteredGuideReviews = guideReviews.filter((r) => {
    const tourMatch = selectedGuideTourFilter === "all" || 
      String(r.tour_listing?.id) === String(selectedGuideTourFilter) || 
      String(r.listing?.id) === String(selectedGuideTourFilter);
    const ratingMatch = selectedGuideRatingFilter === "all" || 
      Math.round(Number(r.rating) || Number(r.rating_type_id) || 5) === Number(selectedGuideRatingFilter);
    return tourMatch && ratingMatch;
  });

  // Filtered Reviews for Hotel Owner
  const filteredHotelReviews = hotelReviews.filter((r) => {
    const listingMatch = selectedHotelListingFilter === "all" || 
      String(r.listing?.id) === String(selectedHotelListingFilter);
    const ratingMatch = selectedHotelRatingFilter === "all" || 
      Math.round(Number(r.rating) || Number(r.rating_type_id) || 5) === Number(selectedHotelRatingFilter);
    return listingMatch && ratingMatch;
  });

  // 1. Fetch User Info
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = paramUserId || userData?.id;
        if (!userId) return;

        const token = localStorage.getItem("jwt_token");
        if (token) {
          try {
            const response = await axios.get(`${BACKEND_URL}/users/${userId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data && response.data.id) {
              setUser(response.data);
              return;
            }
          } catch (e) {
            console.error("API fetch user error, trying fallback:", e);
          }
        }
        if (userData?.id && (!paramUserId || String(paramUserId) === String(userData.id))) {
          setUser(userData);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, [BACKEND_URL, paramUserId]);

  // 2. Fetch Traveler Bookings
  useEffect(() => {
    const fetchBookings = async () => {
      if (!user?.id) return;
      try {
        const token = localStorage.getItem("jwt_token");
        const response = await axios.get(`${BACKEND_URL}/traveler-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          const upcoming = response.data.upcoming || [];
          setBookings({
            upcoming,
            past: response.data.past || [],
            cancelled: response.data.cancelled || [],
          });

          const allDates = [];
          upcoming.forEach((b) => {
            const start = new Date(b.checking_date);
            const end = new Date(b.checkout_date);
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              allDates.push(new Date(d));
            }
          });
          setBookingDates(allDates);
        }
      } catch (err) {
        console.error("Failed to fetch bookings:", err);
      }
    };
    fetchBookings();
  }, [user?.id, BACKEND_URL]);

  // 3. Fetch Agent Status & Details
  useEffect(() => {
    const fetchAgentStatus = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        if (!token) return;

        const response = await axios.get(`${BACKEND_URL}/agents/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAgentStatus(response.data.status);

        if (response.data.status === 4) {
          setIsAgent(true);
          const [details, hotels, guides, hLimit, gLimit] = await Promise.all([
            axios.get(`${BACKEND_URL}/agents/details`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${BACKEND_URL}/agents/hotels`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${BACKEND_URL}/agents/guides`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${BACKEND_URL}/agents/check-hotel-limit`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
            axios.get(`${BACKEND_URL}/agents/check-guide-limit`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          ]);
          setAgentDetails(details.data);
          setAgentHotels(hotels.data);
          setAgentGuides(guides.data);
          if (hLimit?.data) setAgentHotelLimit(hLimit.data);
          if (gLimit?.data) setAgentGuideLimit(gLimit.data);
        }
      } catch (error) {
        console.error("Error fetching agent info", error);
      }
    };
    fetchAgentStatus();
  }, [BACKEND_URL]);

  // 4. Fetch Role Specific Hub Data (Guide/Hotel)
  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem("jwt_token");

    const fetchRoleData = async () => {
      try {
        // Guide Info
        if (isGuideRole(user) && isApprovedRole(user)) {
          const [tours, gBookings, weekly, guideRes, gReviews] = await Promise.all([
            axios.get(`${BACKEND_URL}/guide/${user.id}/listings`).catch(() => ({ data: {} })),
            axios.get(`${BACKEND_URL}/guide/${user.id}/bookings`).catch(() => ({ data: {} })),
            axios.get(`${BACKEND_URL}/guide/${user.id}/bookings/weekly-count`).catch(() => ({ data: {} })),
            axios.get(`${BACKEND_URL}/guide-listing/guide/${user.id}`).catch(() => null),
            axios.get(`${BACKEND_URL}/guide/${user.id}/reviews`).catch(() => ({ data: {} })),
          ]);
          setGuideTours(tours.data?.listings || []);
          setGuideBookings(gBookings.data?.bookings || []);
          setGuideStats({
            activeListings: tours.data?.listings?.length || 0,
            bookingsThisWeek: weekly.data?.weekly_booking_count || 0
          });
          if (guideRes && guideRes.data) {
            setGuideId(guideRes.data.guide_id);
          }
          if (gReviews?.data?.reviews) {
            setGuideReviews(gReviews.data.reviews || []);
            setGuideTotalReviews(gReviews.data.totalReviews || gReviews.data.total_reviews || gReviews.data.reviews.length || 0);
            setGuideAverageRating(gReviews.data.averageRating || gReviews.data.average_rating || 0);
          }
        }

        // Hotel Info
        if (isHotelOwnerRole(user) && isApprovedRole(user)) {
          const [hListings, hBookings, payout, hReviews] = await Promise.all([
            getListingsByOwner(user.id, token).catch(() => ({ success: false })),
            getBookingsByOwner(user.id, token).catch(() => ({ success: false })),
            getTotalPayout(user.id, token).catch(() => ({ success: false })),
            getReviewsByOwner(user.id, token).catch(() => ({ success: false })),
          ]);
          if (hListings.success) setHotelListings(hListings.listings || []);
          if (hBookings.success) setHotelBookings((hBookings.bookings || []).filter(b => b.booking_status_id !== 3));
          if (payout.success) setHotelPayout(payout.totalPayout || 0);
          if (hReviews && hReviews.success) {
            setHotelReviews(hReviews.reviews || []);
            setHotelTotalReviews(hReviews.totalReviews || hReviews.reviews?.length || 0);
            setHotelAverageRating(hReviews.averageRating || 0);
          }
        }
      } catch (err) {
        console.error("Error fetching hub data:", err);
      }
    };
    fetchRoleData();
  }, [user?.id, BACKEND_URL]);

  // 5. Fetch Coin Balance & Referrals
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("jwt_token");
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.id || !token) return;

      try {
        const balance = await getCoinBalance(userData.id, token);
        setCoinBalance(balance);
        const refs = await getMyReferrals(token);
        if (refs.success) setReferrals(refs.referrals);
      } catch (err) {
        console.error("Error fetching coin/referral data", err);
      }
    };
    fetchData();
  }, []);

  // Handlers
  const handleResendVerification = async (userId) => {
    try {
      const token = localStorage.getItem("jwt_token");
      await axios.post(`${BACKEND_URL}/agents/resend-verification-email`, { userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Verification email resent.");
    } catch (error) {
      alert("Failed to resend.");
    }
  };

  const handleLimitRequest = async () => {
    if (!limitReqReason.trim()) {
      alert("Please enter a reason for the request.");
      return;
    }
    if (!user) {
      alert("User not logged in.");
      return;
    }
    try {
      setLimitReqLoading(true);
      const token = localStorage.getItem("jwt_token");

      if (activeTab === "agent") {
        const type = agentLimitType || "hotel";
        await axios.post(`${BACKEND_URL}/agents/limit-increase-request/${type}`, {
          username: user.full_name,
          email: user.email,
          request: limitReqReason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert(`${type === "hotel" ? "Hotel" : "Guide"} registration limit increase request submitted successfully.`);
        // Refresh limits
        if (type === "hotel") {
          const updated = await axios.get(`${BACKEND_URL}/agents/check-hotel-limit`, { headers: { Authorization: `Bearer ${token}` } });
          setAgentHotelLimit(updated.data);
        } else {
          const updated = await axios.get(`${BACKEND_URL}/agents/check-guide-limit`, { headers: { Authorization: `Bearer ${token}` } });
          setAgentGuideLimit(updated.data);
        }
      } else if (activeTab === "guide") {
        if (!guideId) {
          alert("Guide ID not loaded yet. Please try again.");
          return;
        }
        await axios.post(`${BACKEND_URL}/guide-listing/limit-increase-request/${guideId}`, {
          username: user.full_name,
          role: "Guide",
          email: user.email,
          request: limitReqReason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert("Tour listing limit increase request submitted successfully.");
      } else {
        await axios.post(`${BACKEND_URL}/hotels/limit-increase-request/${user.id}`, {
          username: user.full_name,
          role: "Hotel Owner",
          email: user.email,
          request: limitReqReason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert("Hotel listing limit increase request submitted successfully.");
      }

      setIsLimitModalOpen(false);
      setLimitReqReason("");
    } catch (error) {
      alert(error.response?.data?.error || "Failed to submit request.");
    } finally {
      setLimitReqLoading(false);
    }
  };

  const handleChangeEmail = (userId, email) => {
    setEmailUserId(userId);
    setCurrentEmail(email);
    setNewEmail(email);
    setIsEmailModalOpen(true);
  };

  const submitEmailChange = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      await axios.post(`${BACKEND_URL}/agents/change-email`, { userId: emailUserId, newEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Email changed. Check your new inbox.");
      setIsEmailModalOpen(false);
    } catch (error) {
      alert("Failed to change email.");
    }
  };

  const handleInviteFriends = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const result = await generateReferralCode(token);
      const referralLink = `https://soloway.com/register?referralCode=${result.referralCode}`;
      await navigator.clipboard.writeText(referralLink);
      alert("Link copied: " + referralLink);
    } catch (err) {
      alert("Failed to generate link");
    }
  };

  const handleRedeemCoins = async () => {
    try {
      setRedeemLoading(true);
      const token = localStorage.getItem("jwt_token");
      const result = await redeemCoins(user.id, Number(redeemAmount), token);
      setCoinBalance(result.balance);
      alert(result.message);
      setIsRedeemModalOpen(false);
    } catch (error) {
      alert("Redeem failed");
    } finally {
      setRedeemLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      const token = localStorage.getItem("jwt_token");
      const response = await axios.patch(`${BACKEND_URL}/bookings/${bookingId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(prev => ({
        ...prev,
        upcoming: prev.upcoming.filter(b => b.id !== bookingId),
        cancelled: [...prev.cancelled, response.data.booking]
      }));
      alert("Cancelled.");
    } catch (error) {
      alert("Cancel failed.");
    }
  };

  const openBookingModal = (b) => { setSelectedBooking(b); setIsBookingModalOpen(true); };
  const closeBookingModal = () => { setIsBookingModalOpen(false); setSelectedBooking(null); };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const isBooked = bookingDates.some(d => d.toDateString() === date.toDateString());
      if (isBooked) return "booked-date";
    }
    return null;
  };

  return (
    <div className="traveler-dashboard-premium">
      <NavUser />
      
      {/* 🚀 PRODUCTION HERO SECTION */}
      <div className="profile-hero">
        <div className="hero-content">
          <div className="hero-profile-avatar">
            {user?.logo ? (
              <img src={getFileUrl(user.logo, BACKEND_URL)} alt="Avatar" className="avatar-img-premium" />
            ) : <div className="avatar-placeholder-premium">{user?.full_name?.charAt(0) || "T"}</div>}
          </div>
          
          <div className="hero-text">
            <h1 className="hero-name">{user?.full_name || "Traveler"}</h1>
            <p className="hero-meta">
              <FaMapMarkerAlt /> {user?.Country?.name || "Global Explorer"} • {
                user?.Role?.name || 
                (isHotelOwnerRole(user) ? "Hotel Owner" :
                 isGuideRole(user) ? "Guide" :
                 isAgentRole(user, isAgent) ? "Traveler Agent" : "Traveler")
              }
              {((isHotelOwnerRole(user) || isGuideRole(user) || isAgentRole(user, isAgent, paramUserId)) && !isApprovedRole(user) && agentStatus !== 4) && (
                <span 
                  style={{
                    marginLeft: "10px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "rgba(245, 158, 11, 0.2)",
                    color: "#f59e0b",
                    border: "1px solid rgba(245, 158, 11, 0.4)",
                    display: "inline-block"
                  }}
                >
                  Pending Approval
                </span>
              )}
            </p>
            <div className="hero-contact">
              <span><FaEnvelope /> {user?.email}</span>
              <span><FaPhone /> {user?.phone_number}</span>
            </div>
          </div>
          
          <div className="hero-actions">
            <button className="glass-btn primary" onClick={() => navigate("/edit-profile")}>Edit Profile</button>
          </div>
        </div>

        {/* 📊 INTEGRATED STATS BAR */}
        <div className="stats-glass-bar">
          <div className="stat-unit">
            <span className="stat-value-premium">{bookings.upcoming.length + bookings.past.length}</span>
            <span className="stat-label-premium">Total Trips</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-unit">
            <span className="stat-value-premium">{coinBalance}</span>
            <span className="stat-label-premium">Soloway Coins</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-unit">
            <span className="stat-value-premium">{referrals.length}</span>
            <span className="stat-label-premium">Referrals</span>
          </div>
          {isHotelOwnerRole(user) && isApprovedRole(user) && (
            <>
              <div className="stat-divider" />
              <div className="stat-unit">
                <span className="stat-value-premium">★ {hotelAverageRating > 0 ? hotelAverageRating : "5.0"}</span>
                <span className="stat-label-premium">Rating ({hotelTotalReviews})</span>
              </div>
              <div className="stat-divider" />
              <div className="stat-unit">
                <span className="stat-value-premium">${hotelPayout}</span>
                <span className="stat-label-premium">Earnings</span>
              </div>
            </>
          )}
          {isGuideRole(user) && isApprovedRole(user) && (
            <>
              <div className="stat-divider" />
              <div className="stat-unit">
                <span className="stat-value-premium">★ {guideAverageRating > 0 ? guideAverageRating : "5.0"}</span>
                <span className="stat-label-premium">Rating ({guideTotalReviews})</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="container-premium">
        {/* 🧭 NAVIGATION PILL TABS */}
        <div className="production-tabs-wrapper">
          <div className="production-tabs">
            <button 
              className={`prod-tab ${activeTab === 'traveler' ? 'active' : ''}`} 
              onClick={() => setActiveTab('traveler')}
            >
              <FaCompass style={{ marginRight: '6px' }} /> Traveler Hub
            </button>
            {isGuideRole(user) && (
              <button 
                className={`prod-tab ${activeTab === 'guide' ? 'active' : ''}`} 
                onClick={() => setActiveTab('guide')}
              >
                <FaWalking style={{ marginRight: '6px' }} /> Guide Hub 
                {!isApprovedRole(user) && <span className="tab-pending-badge">(Pending)</span>}
              </button>
            )}
            {isHotelOwnerRole(user) && (
              <button 
                className={`prod-tab ${activeTab === 'hotel' ? 'active' : ''}`} 
                onClick={() => setActiveTab('hotel')}
              >
                <FaHotel style={{ marginRight: '6px' }} /> Hotel Hub 
                {!isApprovedRole(user) && <span className="tab-pending-badge">(Pending)</span>}
              </button>
            )}
            {isAgentRole(user, isAgent, paramUserId) && (
              <button 
                className={`prod-tab ${activeTab === 'agent' ? 'active' : ''}`} 
                onClick={() => setActiveTab('agent')}
              >
                <FaUserCog style={{ marginRight: '6px' }} /> Agent Hub 
                {!(agentStatus === 4 || (user?.role_id === 6 && isApprovedRole(user))) && (
                  <span className="tab-pending-badge">(Pending)</span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* 🛠️ HUB CONTENT */}
        <div className="hub-content-area">
          
          {/* 🏔️ TRAVELER HUB */}
          {activeTab === "traveler" && (
            <div className="hub-grid">
              <div className="hub-left">
                <div className="glass-card">
                  <h3 className="premium-title"><FaUserFriends /> About Me</h3>
                  <p className="description-text">{user?.about_me || "A passionate traveler exploring the world with Soloway."}</p>
                </div>

                <div className="glass-card">
                  <h3 className="premium-title"><FaCalendarAlt /> My Schedule</h3>
                  <div className="premium-calendar"><Calendar tileClassName={tileClassName} /></div>
                </div>
              </div>

              <div className="hub-right">
                <div className="glass-card">
                  <h3 className="premium-title"><FaShieldAlt /> Quick Actions</h3>
                  <div className="tiles-grid">
                    <div className="tile-item" onClick={() => navigate("/insurance")}>
                      <div className="tile-icon"><FaShieldAlt /></div>
                      <span>Insurance</span>
                    </div>
                    <div className="tile-item" onClick={() => navigate("/travelerhome")}>
                      <div className="tile-icon"><FaHotel /></div>
                      <span>Book Hotel</span>
                    </div>
                    <div className="tile-item" onClick={() => navigate("/travelerhome")}>
                      <div className="tile-icon"><FaUserFriends /></div>
                      <span>Find Guide</span>
                    </div>
                    <div 
                      className="tile-item" 
                      onClick={() => {
                        // Feature under development: navigate("/visa");
                        Swal.fire({
                          title: "Feature Under Development",
                          text: "The Visa Apply feature is currently under development and will be available soon!",
                          icon: "info",
                          confirmButtonColor: "#0284c7"
                        });
                      }}
                    >
                      <div className="tile-icon"><FaPassport /></div>
                      <span>Apply Visa</span>
                    </div>
                  </div>
                </div>

                <div className="glass-card">
                  <h3 className="premium-title"><FaReceipt /> Bookings</h3>
                  <div className="sub-tabs">
                    {["upcoming", "past", "cancelled"].map(t => (
                      <span key={t} className={`sub-tab ${activePage === t ? 'active' : ''}`} onClick={() => setActivePage(t)}>
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="premium-list">
                    {bookings[activePage]?.length > 0 ? bookings[activePage].map(b => (
                      <div className="list-item-premium" key={b.id}>
                        <div className="list-info">
                          <h4>{b.listing_name}</h4>
                          <span>{new Date(b.checking_date).toLocaleDateString()}</span>
                        </div>
                        <div className="list-actions">
                          <span className="price-tag">${b.price}</span>
                          <button className="circle-btn" onClick={() => openBookingModal(b)} title="View Details">i</button>
                        </div>
                      </div>
                    )) : <p className="empty-msg">No {activePage} bookings found.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 🧭 GUIDE HUB */}
          {activeTab === "guide" && (
            !isApprovedRole(user) ? (
              <div className="glass-card" style={{ textAlign: "center", padding: "50px 20px", margin: "20px 0" }}>
                <div style={{ fontSize: "52px", marginBottom: "15px" }}>⏳</div>
                <h2 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "12px", color: "var(--p-text)" }}>
                  Guide Account Pending Approval
                </h2>
                <p style={{ color: "var(--p-text-muted)", maxWidth: "550px", margin: "0 auto 20px auto", lineHeight: "1.6", fontSize: "15px" }}>
                  Your Tour Guide registration is currently under review by our administration team. Tour listing creation, booking management, and performance analytics will be unlocked once your account has been approved.
                </p>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 18px", borderRadius: "20px", background: "rgba(245, 158, 11, 0.15)", color: "#d97706", fontWeight: "600", fontSize: "14px" }}>
                  <span>Status:</span> <span>Pending Administrator Review</span>
                </div>
              </div>
            ) : (
              <>
                <div className="hub-grid">
                  <div className="hub-left">
                    <div className="glass-card">
                      <h3 className="premium-title"><FaChartLine /> Performance</h3>
                      <div className="mini-stats">
                        <div className="mini-stat"><span>{guideStats.activeListings}</span>Active Tours</div>
                        <div className="mini-stat"><span>{guideStats.bookingsThisWeek}</span>New Bookings</div>
                      </div>
                    </div>
                    <div className="glass-card">
                      <h3 className="premium-title"><FaWalking /> Tour Management</h3>
                      <div style={{ marginBottom: '15px', fontSize: '15px', color: 'var(--p-text-muted)', fontWeight: '600' }}>
                        {guideTours.length}/3 listings filled
                      </div>
                      <button 
                        className="full-btn primary" 
                        onClick={() => navigate("/tourform")}
                        disabled={guideTours.length >= 3}
                        style={guideTours.length >= 3 ? { opacity: 0.5, cursor: 'not-allowed', marginBottom: '10px' } : {}}
                      >
                        List New Tour
                      </button>
                      {guideTours.length >= 3 && (
                        <div style={{ marginTop: '10px', textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '10px' }}>
                          <p style={{ fontSize: '13px', color: '#ff4d4f', margin: '0 0 10px 0', fontWeight: '500' }}>
                            Limit Reached!
                          </p>
                          <button 
                            className="glass-btn primary" 
                            style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '8px', border: '1px solid var(--p-primary)', color: 'var(--p-primary)', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                            onClick={() => setIsLimitModalOpen(true)}
                          >
                            Request Increase
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hub-right">
                    <div className="glass-card">
                      <h3 className="premium-title">My Tour Listings</h3>
                      <div className="premium-list">
                        {guideTours.length > 0 ? (
                          guideTours.map(t => (
                            <div key={t.id} className="list-item-premium">
                              <div className="list-info">
                                <h4>{t.title}</h4>
                                <span className="price-tag">${t.price}</span>
                              </div>
                              <button className="glass-btn sm" onClick={() => navigate(`/tourlisting/${t.public_link}`)}>View</button>
                            </div>
                          ))
                        ) : (
                          <p className="empty-msg">No tour listings found.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ⭐ ALL TOUR LISTING REVIEWS */}
                <div className="glass-card reviews-card-premium" style={{ marginTop: "2rem" }}>
                  <div className="reviews-card-header">
                    <div className="reviews-header-left">
                      <h3 className="premium-title" style={{ margin: 0 }}>
                        <FaStar style={{ color: "#f59e0b" }} /> Tour Listing Reviews
                      </h3>
                      <span className="reviews-count-badge">
                        {guideReviews.length} {guideReviews.length === 1 ? "Review" : "Reviews"}
                      </span>
                    </div>

                    <div className="reviews-score-hero">
                      <span className="score-big">{guideAverageRating > 0 ? guideAverageRating : "5.0"}</span>
                      <div className="score-details">
                        <div className="score-stars">
                          {renderStarRating(guideAverageRating > 0 ? guideAverageRating : 5)}
                        </div>
                        <span className="score-label">Overall Rating</span>
                      </div>
                    </div>
                  </div>

                  {/* Filter Toolbar */}
                  <div className="reviews-filter-toolbar">
                    <div className="filter-select-wrapper">
                      <span className="filter-label"><FaFilter /> Filter Tour:</span>
                      <select 
                        value={selectedGuideTourFilter} 
                        onChange={(e) => setSelectedGuideTourFilter(e.target.value)}
                        className="reviews-filter-select"
                      >
                        <option value="all">All Tours ({guideReviews.length})</option>
                        {guideTours.map((t) => (
                          <option key={t.id} value={t.id}>{t.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="rating-pill-group">
                      {["all", 5, 4, 3, 2, 1].map((score) => (
                        <button
                          key={score}
                          className={`rating-filter-pill ${selectedGuideRatingFilter === String(score) || (score === "all" && selectedGuideRatingFilter === "all") ? "active" : ""}`}
                          onClick={() => setSelectedGuideRatingFilter(String(score))}
                        >
                          {score === "all" ? "All Stars" : `${score} ★`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reviews List */}
                  <div className="reviews-stream">
                    {filteredGuideReviews.length > 0 ? (
                      filteredGuideReviews.map((rev) => (
                        <div key={rev.id} className="review-stream-item">
                          <div className="review-stream-top">
                            <div className="reviewer-meta-box">
                              {rev.user?.logo ? (
                                <img src={getFileUrl(rev.user.logo, BACKEND_URL)} alt={rev.user.full_name} className="reviewer-avatar-img" />
                              ) : (
                                <div className="reviewer-avatar-circle">
                                  {rev.user?.full_name?.charAt(0) || "T"}
                                </div>
                              )}
                              <div>
                                <h4 className="reviewer-name-text">
                                  {rev.user?.full_name || "Verified Traveler"}
                                  <span className="verified-pill"><FaCheckCircle /> Verified Explorer</span>
                                </h4>
                                <div className="reviewer-sub-info">
                                  <span className="listing-tag-pill">{rev.listing?.title || rev.tour_listing?.title || "Tour"}</span>
                                  <span className="review-date-text">{formatReviewDate(rev.created_at || rev.booking_date)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="review-rating-block">
                              <div className="review-stars-row">
                                {renderStarRating(rev.rating || rev.rating_type_id || 5)}
                              </div>
                              <span className="rating-number-tag">{(rev.rating || rev.rating_type_id || 5)}.0</span>
                            </div>
                          </div>
                          <p className="review-feedback-text">"{rev.comment}"</p>
                        </div>
                      ))
                    ) : (
                      <div className="empty-reviews-box">
                        <div className="empty-star-icon">⭐</div>
                        <h4>No Reviews Found</h4>
                        <p>{guideReviews.length === 0 ? "You haven't received any tour reviews yet. Complete bookings will appear here." : "No reviews match the selected filter criteria."}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )
          )}

          {/* 🏨 HOTEL HUB */}
          {activeTab === "hotel" && (
            !isApprovedRole(user) ? (
              <div className="glass-card" style={{ textAlign: "center", padding: "50px 20px", margin: "20px 0" }}>
                <div style={{ fontSize: "52px", marginBottom: "15px" }}>⏳</div>
                <h2 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "12px", color: "var(--p-text)" }}>
                  Hotel Owner Account Pending Approval
                </h2>
                <p style={{ color: "var(--p-text-muted)", maxWidth: "550px", margin: "0 auto 20px auto", lineHeight: "1.6", fontSize: "15px" }}>
                  Your Hotel Owner registration is currently under review by our administration team. Hotel listing creation, room inventory, booking management, and earnings withdrawals will be unlocked once your account has been approved.
                </p>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 18px", borderRadius: "20px", background: "rgba(245, 158, 11, 0.15)", color: "#d97706", fontWeight: "600", fontSize: "14px" }}>
                  <span>Status:</span> <span>Pending Administrator Review</span>
                </div>
              </div>
            ) : (
              <>
                <div className="hub-grid">
                  <div className="hub-left">
                    <div className="glass-card gold">
                      <h3 className="premium-title"><FaMoneyBillWave /> Earnings</h3>
                      <p className="large-payout">${hotelPayout}</p>
                      <button className="glass-btn gold-border" onClick={() => navigate("/withdrawal")}>Withdraw</button>
                    </div>
                    <div className="glass-card">
                      <h3 className="premium-title"><FaHotel /> Inventory</h3>
                      <div style={{ marginBottom: '15px', fontSize: '15px', color: 'var(--p-text-muted)', fontWeight: '600' }}>
                        {hotelListings.length}/3 listings filled
                      </div>
                      <button 
                        className="full-btn primary" 
                        onClick={() => navigate("/hotelform")}
                        disabled={hotelListings.length >= 3}
                        style={hotelListings.length >= 3 ? { opacity: 0.5, cursor: 'not-allowed', marginBottom: '10px' } : {}}
                      >
                        List New Listing
                      </button>
                      {hotelListings.length >= 3 && (
                        <div style={{ marginTop: '10px', textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '10px' }}>
                          <p style={{ fontSize: '13px', color: '#ff4d4f', margin: '0 0 10px 0', fontWeight: '500' }}>
                            Limit Reached!
                          </p>
                          <button 
                            className="glass-btn primary" 
                            style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '8px', border: '1px solid var(--p-primary)', color: 'var(--p-primary)', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                            onClick={() => setIsLimitModalOpen(true)}
                          >
                            Request Increase
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hub-right">
                    <div className="glass-card">
                      <h3 className="premium-title">Managed Hotels</h3>
                      <div className="premium-list">
                        {hotelListings.map(h => (
                          <div 
                            key={h.id} 
                            className="list-item-premium clickable-item" 
                            onClick={() => navigate(`/update-hotel/${h.id}`)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="list-info">
                              <h4>{h.title || h.name}</h4>
                              <span>${h.price} / night</span>
                            </div>
                            <button 
                              className="glass-btn sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/update-hotel/${h.id}`);
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ⭐ ALL HOTEL LISTING REVIEWS */}
                <div className="glass-card reviews-card-premium" style={{ marginTop: "2rem" }}>
                  <div className="reviews-card-header">
                    <div className="reviews-header-left">
                      <h3 className="premium-title" style={{ margin: 0 }}>
                        <FaStar style={{ color: "#f59e0b" }} /> Hotel Listing Reviews
                      </h3>
                      <span className="reviews-count-badge">
                        {hotelReviews.length} {hotelReviews.length === 1 ? "Review" : "Reviews"}
                      </span>
                    </div>

                    <div className="reviews-score-hero">
                      <span className="score-big">{hotelAverageRating > 0 ? hotelAverageRating : "5.0"}</span>
                      <div className="score-details">
                        <div className="score-stars">
                          {renderStarRating(hotelAverageRating > 0 ? hotelAverageRating : 5)}
                        </div>
                        <span className="score-label">Overall Rating</span>
                      </div>
                    </div>
                  </div>

                  {/* Filter Toolbar */}
                  <div className="reviews-filter-toolbar">
                    <div className="filter-select-wrapper">
                      <span className="filter-label"><FaFilter /> Filter Listing:</span>
                      <select 
                        value={selectedHotelListingFilter} 
                        onChange={(e) => setSelectedHotelListingFilter(e.target.value)}
                        className="reviews-filter-select"
                      >
                        <option value="all">All Listings ({hotelReviews.length})</option>
                        {hotelListings.map((h) => (
                          <option key={h.id} value={h.id}>{h.title || h.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="rating-pill-group">
                      {["all", 5, 4, 3, 2, 1].map((score) => (
                        <button
                          key={score}
                          className={`rating-filter-pill ${selectedHotelRatingFilter === String(score) || (score === "all" && selectedHotelRatingFilter === "all") ? "active" : ""}`}
                          onClick={() => setSelectedHotelRatingFilter(String(score))}
                        >
                          {score === "all" ? "All Stars" : `${score} ★`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reviews List */}
                  <div className="reviews-stream">
                    {filteredHotelReviews.length > 0 ? (
                      filteredHotelReviews.map((rev) => (
                        <div key={rev.id} className="review-stream-item">
                          <div className="review-stream-top">
                            <div className="reviewer-meta-box">
                              {rev.user?.logo ? (
                                <img src={getFileUrl(rev.user.logo, BACKEND_URL)} alt={rev.user.full_name} className="reviewer-avatar-img" />
                              ) : (
                                <div className="reviewer-avatar-circle">
                                  {rev.user?.full_name?.charAt(0) || "T"}
                                </div>
                              )}
                              <div>
                                <h4 className="reviewer-name-text">
                                  {rev.user?.full_name || "Verified Guest"}
                                  <span className="verified-pill"><FaCheckCircle /> Verified Guest</span>
                                </h4>
                                <div className="reviewer-sub-info">
                                  <span className="listing-tag-pill">{rev.listing?.title || "Room Listing"}</span>
                                  <span className="review-date-text">{formatReviewDate(rev.created_at)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="review-rating-block">
                              <div className="review-stars-row">
                                {renderStarRating(rev.rating || rev.rating_type_id || 5)}
                              </div>
                              <span className="rating-number-tag">{(rev.rating || rev.rating_type_id || 5)}.0</span>
                            </div>
                          </div>
                          <p className="review-feedback-text">"{rev.comment}"</p>
                        </div>
                      ))
                    ) : (
                      <div className="empty-reviews-box">
                        <div className="empty-star-icon">⭐</div>
                        <h4>No Reviews Found</h4>
                        <p>{hotelReviews.length === 0 ? "You haven't received any guest reviews yet. Completed bookings will appear here." : "No reviews match the selected filter criteria."}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )
          )}

          {/* 💼 AGENT HUB */}
          {activeTab === "agent" && (
            !(agentStatus === 4 || (user?.role_id === 6 && isApprovedRole(user))) ? (
              <div className="glass-card" style={{ textAlign: "center", padding: "50px 20px", margin: "20px 0" }}>
                <div style={{ fontSize: "52px", marginBottom: "15px" }}>⏳</div>
                <h2 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "12px", color: "var(--p-text)" }}>
                  Travel Agent Account Pending Approval
                </h2>
                <p style={{ color: "var(--p-text-muted)", maxWidth: "550px", margin: "0 auto 20px auto", lineHeight: "1.6", fontSize: "15px" }}>
                  Your Travel Agent registration is currently under review by our administration team. Hotel registration, tour guide onboarding, and commission tracking will be unlocked once your account has been approved.
                </p>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 18px", borderRadius: "20px", background: "rgba(245, 158, 11, 0.15)", color: "#d97706", fontWeight: "600", fontSize: "14px" }}>
                  <span>Status:</span> <span>Pending Administrator Review</span>
                </div>
              </div>
            ) : (
              <div className="hub-grid">
                <div className="hub-left">
                  <div className="glass-card">
                    <h3 className="premium-title">Agent Tools</h3>
                    <div className="agent-links">
                      <Link to="/agent-hotel-register" className="tool-link"><FaHotel /> Register Hotel</Link>
                      <Link to="/agent-guide-register" className="tool-link"><FaWalking /> Register Guide</Link>
                      <Link to="/agent-commission-withdrawal" className="tool-link"><FaMoneyBillWave /> Commissions</Link>
                    </div>
                  </div>
                </div>
                <div className="hub-right">
                  <div className="glass-card">
                    <h3 className="premium-title">Entities Managed</h3>
                    
                    {/* Managed Hotels Section */}
                    <div className="entity-section">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                        <h4 className="entity-section-title" style={{ margin: 0 }}>
                          Hotel Owners ({agentHotels.length} / {agentHotelLimit?.limit ?? 3})
                        </h4>
                        {agentHotelLimit?.hasPendingRequest ? (
                          <span style={{ fontSize: "12px", background: "rgba(245, 158, 11, 0.15)", color: "#d97706", padding: "4px 10px", borderRadius: "12px", fontWeight: "600" }}>
                            Limit Request Pending
                          </span>
                        ) : (
                          <button 
                            className="glass-btn sm" 
                            style={{ fontSize: "12px", padding: "4px 10px" }}
                            onClick={() => {
                              setAgentLimitType("hotel");
                              setIsLimitModalOpen(true);
                            }}
                          >
                            + Request Limit Increase
                          </button>
                        )}
                      </div>
                      <div className="entity-list">
                        {agentHotels.length > 0 ? (
                          agentHotels.map(h => {
                            const statusId = h.status_id || h.User?.status_id || 3; // default to pending
                            const isApproved = statusId === 4;
                            return (
                              <div 
                                key={h.id} 
                                className={`list-item-premium ${isApproved ? 'clickable-entity' : ''}`}
                                onClick={() => {
                                  if (isApproved) {
                                    navigate(`/agent-hotelowner-profile/${h.user_id}/${agentDetails?.id}`);
                                  }
                                }}
                                title={isApproved ? "Click to view profile & listings" : "Pending administrator approval"}
                              >
                                <div className="list-info">
                                  <h4>{h.name}</h4>
                                  <span>{h.User?.email}</span>
                                </div>
                                <div className="list-item-premium-actions">
                                  <span className={`status-badge ${statusId === 4 ? 'status-approved' : statusId === 3 ? 'status-pending' : statusId === 2 ? 'status-suspended' : 'status-inactive'}`}>
                                    {statusId === 4 ? 'Approved' : statusId === 3 ? 'Pending' : statusId === 2 ? 'Suspended' : 'Inactive'}
                                  </span>
                                  {!h.User?.is_verified && (
                                    <button 
                                      className="glass-btn sm warn" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResendVerification(h.user_id);
                                      }}
                                    >
                                      Resend
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="empty-msg">No managed hotels registered yet.</p>
                        )}
                      </div>
                    </div>

                    {/* Managed Guides Section */}
                    <div className="entity-section" style={{ marginTop: "2rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                        <h4 className="entity-section-title" style={{ margin: 0 }}>
                          Tour Guides ({agentGuides.length} / {agentGuideLimit?.limit ?? 3})
                        </h4>
                        {agentGuideLimit?.hasPendingRequest ? (
                          <span style={{ fontSize: "12px", background: "rgba(245, 158, 11, 0.15)", color: "#d97706", padding: "4px 10px", borderRadius: "12px", fontWeight: "600" }}>
                            Limit Request Pending
                          </span>
                        ) : (
                          <button 
                            className="glass-btn sm" 
                            style={{ fontSize: "12px", padding: "4px 10px" }}
                            onClick={() => {
                              setAgentLimitType("guide");
                              setIsLimitModalOpen(true);
                            }}
                          >
                            + Request Limit Increase
                          </button>
                        )}
                      </div>
                      <div className="entity-list">
                        {agentGuides.length > 0 ? (
                          agentGuides.map(g => {
                            const statusId = g.status_id || g.user?.status_id || 3; // default to pending
                            const isApproved = statusId === 4;
                            return (
                              <div 
                                key={g.id} 
                                className={`list-item-premium ${isApproved ? 'clickable-entity' : ''}`}
                                onClick={() => {
                                  if (isApproved) {
                                    navigate(`/agent-guide-profile/${g.user_id}/${agentDetails?.id}`);
                                  }
                                }}
                                title={isApproved ? "Click to view profile & listings" : "Pending administrator approval"}
                              >
                                <div className="list-info">
                                  <h4>{g.name}</h4>
                                  <span>{g.user?.email}</span>
                                </div>
                                <div className="list-item-premium-actions">
                                  <span className={`status-badge ${statusId === 4 ? 'status-approved' : statusId === 3 ? 'status-pending' : statusId === 2 ? 'status-suspended' : 'status-inactive'}`}>
                                    {statusId === 4 ? 'Approved' : statusId === 3 ? 'Pending' : statusId === 2 ? 'Suspended' : 'Inactive'}
                                  </span>
                                  {!g.user?.is_verified && (
                                    <button 
                                      className="glass-btn sm warn" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResendVerification(g.user_id);
                                      }}
                                    >
                                      Resend
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="empty-msg">No managed guides registered yet.</p>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )
          )}

        </div>
      </div>

      {/* 🔮 MODALS (PREMIUM STYLED) */}
      <Modal isOpen={isRedeemModalOpen} onRequestClose={() => setIsRedeemModalOpen(false)} className="premium-modal" overlayClassName="modal-overlay">
        <div className="modal-header"><h3>Redeem Your Wealth</h3></div>
        <div className="modal-body">
          <p>You have <strong>{coinBalance}</strong> Soloway Coins available.</p>
          <input type="number" placeholder="Amount to redeem" value={redeemAmount} onChange={e => setRedeemAmount(e.target.value)} className="premium-input" />
          <div className="modal-footer">
            <button className="glass-btn" onClick={() => setIsRedeemModalOpen(false)}>Cancel</button>
            <button className="full-btn primary" onClick={handleRedeemCoins} disabled={redeemLoading}>{redeemLoading ? "Processing..." : "Redeem Now"}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isBookingModalOpen} onRequestClose={closeBookingModal} className="premium-modal" overlayClassName="modal-overlay">
        <div className="modal-header"><h3>Booking Overview</h3></div>
        {selectedBooking && (
          <div className="modal-body">
            <div className="detail-row"><span>Listing</span><strong>{selectedBooking.listing_name}</strong></div>
            <div className="detail-row"><span>ID</span><strong>#{selectedBooking.id}</strong></div>
            <div className="detail-row"><span>Check-in</span><strong>{new Date(selectedBooking.checking_date).toLocaleDateString()}</strong></div>
            <div className="detail-footer">
              <button className="full-btn primary" onClick={closeBookingModal}>Close</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={isLimitModalOpen} onRequestClose={() => setIsLimitModalOpen(false)} className="premium-modal" overlayClassName="modal-overlay">
        <div className="modal-header">
          <h3>
            {activeTab === "agent"
              ? `Request Agent ${agentLimitType === "hotel" ? "Hotel" : "Guide"} Limit Increase`
              : activeTab === "guide"
              ? "Request Tour Listing Limit Increase"
              : "Request Hotel Listing Limit Increase"}
          </h3>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: "15px", color: "var(--p-text-muted)", fontSize: "14px" }}>
            {activeTab === "agent"
              ? `Submit a request to increase your allowable registered ${agentLimitType === "hotel" ? "hotel" : "guide"} count.`
              : "Submit a request to increase your allowable listing count."}
          </p>
          <textarea 
            placeholder="Reason for request (e.g., managing more properties/guides)..." 
            value={limitReqReason} 
            onChange={e => setLimitReqReason(e.target.value)} 
            className="premium-input" 
            style={{ width: "100%", height: "100px", padding: "10px", borderRadius: "8px", border: "1px solid rgba(0,0,0,0.1)", marginBottom: "15px", fontFamily: "inherit" }}
          />
          <div className="modal-footer">
            <button className="glass-btn" onClick={() => setIsLimitModalOpen(false)}>Cancel</button>
            <button className="full-btn primary" onClick={handleLimitRequest} disabled={limitReqLoading}>
              {limitReqLoading ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TravelerProfile;
